import * as Xstate from "@xstate/react";
import React, { useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { GlobalContext } from "../../../app/stateMachines/GlobalContext";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { useAppDispatch, useAppSelector } from "../../../redux/redux";
import { getUser } from "../../../services/AppService";
import AddPayment from "../payments/AddPayment";
import PayInvoice from "../payments/PayInvoice";
import { Payments } from "../payments/Payments";
import PaymentView, { EditPaymentPage } from "../payments/PaymentView";
import { BillingHeader } from "../ui/BillingUi";
import AddInvoice from "./AddInvoice";
import { InvoicesListView } from "./InvoicesListView";
import InvoiceView from "./InvoiceView";

/** Billing pages: header band with tabs, then the invoice/payment routes. */
export const BillingPages: React.FC<{ hasAccess: boolean }> = ({
  hasAccess,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeTab = pathname.includes("/billing/payments")
    ? "payments"
    : "invoices";
  // An invoice/payment page or form (not the list) gets a back button.
  const isSubPage = /\/billing\/(invoices|payments)\/.+/.test(pathname);
  const isDetailPage = isSubPage && !/\/(add|edit|pay\/[^/]+)\/?$/.test(pathname);

  return (
    // Full-width band: undo the main view's padding so the white header and
    // grey page background reach the edges, as in the design.
    <div
      className={
        "-mx-8 -my-4 min-h-[calc(100vh-64px)] bg-[#F7F8FA] pb-16 font-Manrope max-md:-mx-4"
      }>
      <BillingHeader
        activeTab={activeTab}
        onTabClick={(tab) => navigate(`/billing/${tab}`)}
        onBack={isSubPage ? () => navigate(-1) : undefined}
        onClose={
          isDetailPage ? () => navigate(`/billing/${activeTab}`) : undefined
        }
      />
      <div className={"px-8 pt-[26px] max-md:px-4"}>
        {hasAccess ? (
          <Routes>
            <Route
              index
              element={<Navigate to={"./invoices"} replace={true} />}
            />
            <Route path={"invoices"} element={<Outlet />}>
              <Route index element={<InvoicesListView />} />
              <Route path={":id"} element={<InvoiceView />} />
              <Route path={"add"} element={<AddInvoice />} />
            </Route>
            <Route path={"payments"} element={<Outlet />}>
              <Route index element={<Payments />} />
              <Route path={":id"} element={<PaymentView />} />
              <Route path={":id/edit"} element={<EditPaymentPage />} />
              <Route path={"add"} element={<AddPayment />} />
              <Route path={"pay/:id"} element={<PayInvoice />} />
            </Route>
            <Route path={"open"} element={<>Todo open invoices</>} />
            <Route path={"closed"} element={<>Todo closed invoices</>} />
            <Route path={"*"} element={<>Not found</>} />
          </Routes>
        ) : (
          <div
            className={
              "flex h-[75vh] items-center justify-center text-[42px] font-bold text-gray-400"
            }>
            You don`t have access to this page
          </div>
        )}
        <div>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export const InvoicesView: React.FC<unknown> = () => {
  const dispatch = useAppDispatch();

  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const { user, isLoadingUser } = useAppSelector((state) => state.appReducer);
  useEffect(() => {
    dispatch(
      getUser({
        userId: `${userInfoByEmail?.id}`,
        orgId: `${userInfoByEmail?.org?.id}`,
      }),
    );
  }, []);
  const viewBillingsPermission = "434d4588-3532-4e3a-ac3c-d294ef2d76a6";

  if (isLoadingUser) {
    return <LottieLoading />;
  }
  return (
    <BillingPages
      hasAccess={
        !!user.permissionIds?.includes(viewBillingsPermission) ||
        user?.userRoleEnum?.userRole === "Admin"
      }
    />
  );
};
